import type { JSX, ReactNode } from 'react'
import { Button } from 'primereact/button'
import { TableSkeleton } from '../data/TableSkeleton.js'
import { EmptyState } from '../data/EmptyState.js'
import SearchBox from './SearchBox.js'
import ActionButtons from './ActionButtons.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ColumnDef<T> {
  /** Column header label. */
  label: string
  /**
   * Either a key of T (whose value will be rendered as a string) or a render
   * function that returns arbitrary JSX.
   */
  accessor: keyof T | ((row: T) => ReactNode)
  /**
   * When provided, enables sorting on this column even when `accessor` is a
   * render function. The value is the field name PrimeReact sorts by.
   */
  sortField?: string
}

export interface PaginationConfig {
  total: number
  limit: number
  offset: number
  onChange: (next: { limit: number; offset: number }) => void
}

export interface SortConfig {
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onChange: (next: { sortBy: string; sortOrder: 'asc' | 'desc' }) => void
}

export interface SearchConfig {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export interface VirtualScrollConfig {
  itemSize: number
  lazy?: boolean
  onLazyLoad?: (event: unknown) => void
}

export interface EmptyConfig {
  title: string
  description?: string
  icon?: string
  action?: ReactNode
}

/** Per-row actions a table may render. Defaults to both. */
export type ResourceTableAction = 'edit' | 'copy' | 'delete' | 'view'

export interface ResourceTableProps<T extends { id: string }> {
  columns: ColumnDef<T>[]
  /** The list of items to render. Typically the `items` array returned by useAdminResource. */
  rows: T[]
  loading?: boolean
  /** Called when the Edit action is clicked for a row. Required when `actions` includes 'edit'. */
  onEdit?: (row: T) => void
  /**
   * Called when the Copy action is clicked for a row. Opens the create drawer
   * pre-filled with the source row's values (with a "Copy of " name prefix) so
   * the user can save a duplicate. Required when `actions` includes 'copy'.
   */
  onCopy?: (row: T) => void
  /**
   * Called when the Delete action is clicked for a non-deleted row.
   * Optional: resources that cannot be deleted (e.g. campaigns) omit this and
   * pass `actions={['edit']}` so the Delete button is not rendered.
   */
  onDelete?: (row: T) => void
  /** Called when the View action is clicked for a row. Required when `actions` includes 'view'. */
  onView?: (row: T) => void
  /**
   * Which per-row actions to render. Defaults to `['edit', 'delete']`.
   * Pass `['edit']` to hide the Delete button entirely. Pass `[]` (an empty
   * array) to suppress the trailing "Actions" column entirely — for consumers
   * that render their own actions as a regular render-function column.
   */
  actions?: ResourceTableAction[]
  /**
   * Called when the Restore action is clicked for a deleted row.
   * Optional: hard-delete resources (no soft-delete) omit this, and the
   * Restore action is then not rendered.
   */
  onRestore?: (row: T) => void

  // --- Optional props (all callers continue to work without them) ---

  /**
   * When provided and `loading=true`, replaces the inline skeleton with the
   * Bundle-A `TableSkeleton` component. If absent the component falls back to
   * the original inline skeleton so existing callers are unaffected.
   */
  empty?: EmptyConfig

  pagination?: PaginationConfig

  sort?: SortConfig

  search?: SearchConfig

  /**
   * Passed through to the underlying scrollable container.  Currently stored
   * for forward-compat; PrimeReact DataTable adoption can wire it up later.
   */
  virtualScroll?: VirtualScrollConfig
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A row is considered soft-deleted when deletedAt is set or status==='deleted'. */
function isDeleted(row: unknown): boolean {
  const r = row as { deletedAt?: string | null; status?: string }
  if (r.deletedAt != null) return true
  if (r.status === 'deleted') return true
  return false
}

function renderCell<T>(row: T, accessor: ColumnDef<T>['accessor']): ReactNode {
  if (typeof accessor === 'function') {
    return accessor(row)
  }
  const val = row[accessor]
  if (val === null || val === undefined) return '—'
  return String(val)
}

function columnField<T>(col: ColumnDef<T>): string | undefined {
  if (col.sortField) return col.sortField
  if (typeof col.accessor === 'function') return undefined
  return String(col.accessor)
}

// ---------------------------------------------------------------------------
// Pagination bar
// ---------------------------------------------------------------------------

function PaginationBar({ config }: { config: PaginationConfig }): JSX.Element | null {
  const { total, limit, offset, onChange } = config
  if (total <= 0) return null
  const totalPages = Math.ceil(total / limit) || 1
  const currentPage = Math.floor(offset / limit) + 1

  function goToPage(page: number): void {
    const newOffset = (page - 1) * limit
    onChange({ limit, offset: newOffset })
  }

  return (
    <div className="bg-white border-t border-gray-100 px-5 py-3 flex items-center justify-between text-sm text-gray-500">
      <div>
        Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} records
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage <= 1}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Previous
        </button>
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number
            if (totalPages <= 5) {
              pageNum = i + 1
            } else if (currentPage <= 3) {
              pageNum = i + 1
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i
            } else {
              pageNum = currentPage - 2 + i
            }
            return (
              <button
                type="button"
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-all ${
                  pageNum === currentPage
                    ? 'bg-[#3454cf] text-white'
                    : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Next
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sort header cell
// ---------------------------------------------------------------------------

const TH_BASE =
  'px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase bg-white border-b border-gray-100 whitespace-nowrap'

function SortableTh({
  label,
  field,
  sort,
}: {
  label: string
  field?: string
  sort?: SortConfig
}): JSX.Element {
  if (!sort || !field) {
    return <th className={TH_BASE}>{label}</th>
  }

  const isActive = sort.sortBy === field
  const nextOrder: 'asc' | 'desc' = isActive && sort.sortOrder === 'asc' ? 'desc' : 'asc'
  const indicator = isActive ? (sort.sortOrder === 'asc' ? ' ▲' : ' ▼') : ''

  return (
    <th
      className={`${TH_BASE} cursor-pointer select-none hover:text-gray-600`}
      onClick={() => sort.onChange({ sortBy: field, sortOrder: nextOrder })}
      data-testid={`sort-header-${field}`}
    >
      {label}
      <span className="ml-1 text-[10px] opacity-60">{indicator}</span>
    </th>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Generic table for tenant-admin resource lists.
 *
 * - Greys out rows that are soft-deleted (`deletedAt != null` or `status === 'deleted'`).
 * - Shows Edit + Delete/Restore action buttons per row.
 * - Shows skeleton rows while loading and an empty state when there are no items.
 *
 * Styling now matches the users-tab / `AdminListScaffold` DataTable look:
 * tighter header cells, hover rows, circular lucide action icons, and the same
 * manual pagination pattern.
 */
export default function ResourceTable<T extends { id: string }>({
  columns,
  rows,
  loading = false,
  onEdit,
  onCopy,
  onDelete,
  onView,
  onRestore,
  actions = ['edit', 'delete'],
  empty,
  pagination,
  sort,
  search,
  virtualScroll: _virtualScroll,
}: ResourceTableProps<T>): JSX.Element {
  const showDelete = actions.includes('delete')
  const showActionsColumn = actions.length > 0
  const totalColumns = columns.length + (showActionsColumn ? 1 : 0)

  if (loading) {
    return <TableSkeleton rows={5} columns={totalColumns} />
  }

  const tableContent = (
    <table className="w-full min-w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-gray-100">
          {columns.map((col) => {
            const field = columnField(col)
            return (
              <SortableTh
                key={String(col.label)}
                label={col.label}
                {...(field !== undefined ? { field } : {})}
                {...(sort !== undefined ? { sort } : {})}
              />
            )
          })}
          {showActionsColumn && (
            <th className={TH_BASE} style={{ width: '120px' }}>
              Actions
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          empty ? (
            <tr>
              <td colSpan={totalColumns} className="py-8">
                <EmptyState
                  title={empty.title}
                  {...(empty.description ? { description: empty.description } : {})}
                  {...(empty.icon ? { icon: empty.icon } : {})}
                  {...(empty.action ? { action: empty.action } : {})}
                />
              </td>
            </tr>
          ) : (
            <tr>
              <td
                colSpan={totalColumns}
                className="px-5 py-10 text-center text-sm text-gray-400"
              >
                No items found.
              </td>
            </tr>
          )
        ) : (
          rows.map((row, index) => {
            const deleted = isDeleted(row)
            const even = index % 2 === 0
            return (
              <tr
                key={row.id}
                data-testid={`resource-row-${row.id}`}
                className={[
                  'bg-white border-b border-gray-50 transition-colors duration-150',
                  deleted
                    ? 'text-gray-400'
                    : even
                      ? 'hover:bg-gray-50/50'
                      : 'hover:bg-gray-50/50',
                ].join(' ')}
              >
                {columns.map((col) => (
                  <td
                    key={String(col.label)}
                    className={[
                      'py-3.5 px-5 text-sm whitespace-nowrap border-b border-gray-50',
                      deleted ? 'line-through text-gray-400' : 'text-gray-700',
                    ].join(' ')}
                  >
                    {renderCell(row, col.accessor)}
                  </td>
                ))}
                {showActionsColumn && (
                  <td className="py-3.5 px-5 align-middle" style={{ width: '120px' }}>
                    <span className="inline-flex items-center gap-1">
                      {!deleted && (actions.includes('view') || actions.includes('edit') || actions.includes('copy') || showDelete) && (
                        <ActionButtons
                          {...(actions.includes('view') ? { onView: () => { onView?.(row) } } : {})}
                          {...(actions.includes('edit') ? { onEdit: () => { onEdit?.(row) } } : {})}
                          {...(actions.includes('copy') && onCopy ? { onCopy: () => { onCopy(row) } } : {})}
                          {...(showDelete && onDelete ? { onDelete: () => { onDelete(row) } } : {})}
                        />
                      )}
                      {deleted && onRestore ? (
                        <Button
                          type="button"
                          icon="pi pi-undo"
                          rounded
                          severity="success"
                          size="small"
                          aria-label="Restore"
                          onClick={() => onRestore(row)}
                        />
                      ) : null}
                    </span>
                  </td>
                )}
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  )

  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
      {search && (
        <div className="px-5 pt-4">
          <SearchBox
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder ?? 'Search...'}
            dataTestId="resource-table-search"
          />
        </div>
      )}
      <div className="overflow-x-auto">{tableContent}</div>
      {pagination && <PaginationBar config={pagination} />}
    </div>
  )
}
